// Question Extraction run detail — Create Assignment from PDF/Word.
//
// Mirrors GenerationDetail/ConceptLabDetail using the shared operation-detail
// components. Reads the DEDICATED `question-extraction-runs` record rather than
// the async job: the job is a generic 7-day execution trail, the run record is
// the durable account of what this import read and produced.
//
// Extraction has no student and no source question — the "question" on its
// model inputs is a synthetic placeholder — so those sections are dropped. What
// matters instead is the source file, the counters, and the per-run prompt +
// parsed output, where the extracted questions render with their diagram boxes
// overlaid on the pages the model actually saw.
//
// Model runs are keyed by submissionId = the async job id, which is why the run
// record keeps `asyncJobId`.
import { useEffect, useState } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { modelApi } from '../../api/modelApi';
import { moveToTenantPath } from '../../api/auth';
import type { ModelOutput } from '../../types';
import {
    OperationDetailPage,
    ContextGrid,
    MetadataCard,
    MetaItem,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
    formatCost,
    totalRunCost,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import './SolutionAnalysisDetail.css';

export function ExtractionDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    const { data: runData, isLoading, error } = useQuery({
        queryKey: ['questionExtractionRun', id],
        queryFn: () => adminApi.getQuestionExtractionRun(id!),
        enabled: !!id,
    });

    const run = runData?.run;

    // Model runs are recorded against the async job id, not the run record id.
    const { data: runs = [] } = useQuery({
        queryKey: ['modelRuns', run?.asyncJobId],
        queryFn: () => adminApi.getModelRuns(run!.asyncJobId!),
        enabled: !!run?.asyncJobId,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });
    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    // The page images this run attached live on the MODEL INPUT's
    // operationContext (extraction has no student solution image), and they are
    // the overlay target for the extracted questions' diagram boxes.
    const selectedInputId = runs.find(r => r.id === selectedRunId)?.inputId;
    const { data: modelInput } = useQuery({
        queryKey: ['modelInput', selectedInputId],
        queryFn: () => modelApi.getModelInput(selectedInputId!),
        enabled: !!selectedInputId,
    });

    // The URL's tenant is wherever the admin signed in; the run says whose
    // import this is. Put the page under the run's own tenant.
    useEffect(() => {
        if (run) moveToTenantPath(run.tenantSlug);
    }, [run]);

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find(r => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const goBack = () => navigate('/submissions?tab=extraction');

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading extraction run…</div></div>;
    }
    if (error || !run) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading extraction run: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={goBack}>Back to Question Extraction</button>
            </div>
        );
    }

    const createdCount = run.createdQuestionIds?.length ?? run.extracted ?? 0;
    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Question Extraction Detail' },
        // Enrichment runs in batches — show which questions each batch covered.
        info: r => r.runInfo ?? null,
    });
    const selectedRun = runs.find(r => r.id === selectedRunId);
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRunRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRunRawResponse));


    const parsedBody = selectedRun && parsedHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRunRawResponse}
            pageImages={modelInput?.operationContext?.attachedImages}
        />
    ) : undefined;

    // Any question that missed enrichment reaches the teacher with no answer
    // and no solution, so it is called out rather than left to arithmetic.
    const enrichmentFailed = run.enrichmentFailed ?? 0;
    const validationFlagged = (run.validationWrong ?? 0) + (run.validationSuspect ?? 0);

    return (
        <OperationDetailPage
            title="Question Extraction Details"
            id={run.id}
            backLabel="Question Extraction"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Extraction" />}
            onBack={goBack}
            context={
                <>
                <ContextGrid
                    left={
                        <MetadataCard title="Source">
                            <MetaItem label="Type">
                                <span className="tag">{run.sourceKind === 'docx' ? 'Word (.docx)' : 'PDF'}</span>
                            </MetaItem>
                            <MetaItem label="File">
                                {run.sourceFileUrl
                                    ? <a href={run.sourceFileUrl} target="_blank" rel="noopener noreferrer">{run.sourceFileName || 'source file'} ↗</a>
                                    : (run.sourceFileName || '—')}
                            </MetaItem>
                            <MetaItem label="Tenant">
                                {run.tenantName || run.tenantId || '—'}
                                {run.tenantName && run.tenantId && <span className="muted"> · {run.tenantId}</span>}
                            </MetaItem>
                            <MetaItem label="Teacher">
                                {run.teacherName || run.createdBy}
                                {run.teacherEmail && <span className="muted"> · {run.teacherEmail}</span>}
                            </MetaItem>
                            <MetaItem label="Assignment">
                                {run.correlation?.questionSetId
                                    ? <a role="button" style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/question-sets/${run.correlation!.questionSetId}`)}>
                                        {run.correlation.questionSetId} →
                                    </a>
                                    : <span className="muted">none</span>}
                            </MetaItem>
                        </MetadataCard>
                    }
                    right={
                        <MetadataCard title="Run">
                            <MetaItem label="Status"><span className={`tag status-${run.status}`}>{run.status}</span></MetaItem>
                            {run.pages && (
                                <MetaItem label="Pages">
                                    {run.pages.read} read
                                    {run.pages.dropped > 0 && (
                                        <span className="tag status-failed" style={{ marginLeft: 6 }}>
                                            {run.pages.dropped} over the cap, not read
                                        </span>
                                    )}
                                </MetaItem>
                            )}
                            <MetaItem label="Latency">{formatLatency(run.latencyMs)}</MetaItem>
                            <MetaItem label="Started">
                                <span className="datetime">{format(new Date(run.startedAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                            {run.completedAt && (
                                <MetaItem label="Completed">
                                    <span className="datetime">{format(new Date(run.completedAt), 'MMM dd, HH:mm:ss')}</span>
                                </MetaItem>
                            )}
                            {run.llm && (
                                <MetaItem label="Tokens / Cost">
                                    {run.llm.inputTokens.toLocaleString()} in / {run.llm.outputTokens.toLocaleString()} out
                                    {/* Every call of the operation (the runs below), not the record's own figure. */}
                                    {(totalRunCost(runs) ?? run.llm.costUsd) != null && <span className="muted"> · {formatCost(totalRunCost(runs) ?? run.llm.costUsd!)}</span>}
                                </MetaItem>
                            )}
                            {run.error && (
                                <MetaItem label="Error"><span className="tag status-failed">{run.error}</span></MetaItem>
                            )}
                        </MetadataCard>
                    }
                />
                <MetadataCard title="Extracted Output">
                    <MetaItem label="Questions">
                        {createdCount > 0 ? (
                            <a role="button" style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/questions?batch=${run.asyncJobId ?? run.id}&source=teacher`)}>
                                Show {createdCount} extracted question{createdCount === 1 ? '' : 's'} →
                            </a>
                        ) : <span className="muted">none created</span>}
                    </MetaItem>
                    <MetaItem label="With Diagram">{run.withDiagram ?? '—'}</MetaItem>
                    <MetaItem label="Enriched">
                        {run.enriched == null || run.extracted == null
                            ? '—'
                            : (
                                <span className={enrichmentFailed > 0 ? 'tag status-failed' : undefined}>
                                    {run.enriched}/{run.extracted}
                                    {enrichmentFailed > 0 && ` — ${enrichmentFailed} question${enrichmentFailed === 1 ? '' : 's'} have no answer or solution`}
                                </span>
                            )}
                    </MetaItem>
                </MetadataCard>
                {/* Only when a judge step ran. Its absence means the pipeline
                    carries no `validate_extraction` step — the normal case —
                    not that the extraction was found clean. */}
                {run.validated != null && (
                    <MetadataCard title="Validation Judge">
                        <MetaItem label="Checked">
                            <span className={validationFlagged > 0 ? 'tag status-failed' : undefined}>
                                {run.validated} question{run.validated === 1 ? '' : 's'}
                                {validationFlagged > 0
                                    ? ` — ${run.validationWrong ?? 0} wrong, ${run.validationSuspect ?? 0} suspect`
                                    : ' — no discrepancies found'}
                            </span>
                        </MetaItem>
                        {/* Separate from "missing numbers": this catches questions
                            lost off the start or end of a page range, where the
                            returned numbers stay contiguous. */}
                        <MetaItem label="Missed">
                            {run.validationMissed
                                ? <span className="tag status-failed">
                                    {run.validationMissed} question{run.validationMissed === 1 ? '' : 's'} on the page were never extracted
                                </span>
                                : <span className="muted">none</span>}
                        </MetaItem>
                        {run.validationCostUsd != null && (
                            <MetaItem label="Judge Cost">
                                <span className="muted">${run.validationCostUsd.toFixed(4)} of the run total</span>
                            </MetaItem>
                        )}
                        {run.validationIssues?.map((issue, i) => (
                            <MetaItem
                                key={i}
                                label={issue.questionNumber ? `Q${issue.questionNumber}` : `#${(issue.slot ?? i) + 1}`}
                            >
                                <span className={issue.verdict === 'wrong' ? 'tag status-failed' : undefined}>
                                    {issue.verdict} · {issue.field} · {issue.severity}
                                </span>
                                <div>{issue.detail}</div>
                                {issue.suggestion && <div className="muted">Suggested: {issue.suggestion}</div>}
                            </MetaItem>
                        ))}
                    </MetadataCard>
                )}
                </>
            }
            runs={runs.length > 0 ? (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={r => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={r => setSelectedRunId(r.id)}
                    getRowClass={r => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            ) : (
                <div className="card"><p className="muted">No model runs recorded for this extraction.</p></div>
            )}
            runDetail={
                selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={runError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRunRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedBody}
                    />
                )
            }
        />
    );
}
